import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, ViewEncapsulation, WritableSignal, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { Party } from '@app/dashboard/models/document-details.model';
import { IDRPartyRepresentationDTO } from '@app/dashboard/models/document-reception.model';
import { DocumentReceptionService } from '@app/dashboard/services/document-reception.service';
import { TAny } from '@app/lib/models/common.type';
import { IResponse } from '@app/lib/models/response.model';
import { DialogService } from '@app/lib/services/dialog.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

@Component({
  selector: 'app-add-party-dialog',
  templateUrl: './add-party-dialog.component.html',
  styleUrls: ['./add-party-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatTableModule,
    MatCheckboxModule,
    MatIconModule,
    MatFormFieldModule,  // Ensure MatFormFieldModule is imported
    MatInputModule,MatProgressSpinnerModule  
  ],
  encapsulation: ViewEncapsulation.None, 
})
export class AddPartyDialogComponent implements OnInit{
  public partiesData: WritableSignal<Party[]> = signal([]); 
  loading:boolean=false;
  currentpage:number=1;
  isScroll:boolean=false;
  totalItems=0;
  displayedColumns: string[] = [
    'prtType',
    'partiesId',
    'partiesFullName',
    'select',
  ];
    selectedRow: any;
    searchQuery: string = '';
    isLoading: boolean = false;
    private searchSubject = new Subject<string>(); // Subject for handling search input
  

  constructor(
    private dialogRef: MatDialogRef<AddPartyDialogComponent>,
    private service: DocumentReceptionService,
    private router: Router,
    private dialogService: DialogService,
    @Inject(MAT_DIALOG_DATA)
    public data:TAny) {
      this.data.multi = this.data.multi ?? false;
    }
   

      // To track the loading state
      ngOnInit() {
        this.getListOfParties(); // Load first batch

        this.searchSubject
        .pipe(
          debounceTime(300), // Wait for 300ms after the last keystroke
          distinctUntilChanged() // Only emit if the value changes
        )
        .subscribe((query) => {
          if (query.length >= 4) {
            this.searchQuery = query; // Update the search query
            this.currentpage = 1; // Reset pagination
            this.partiesData.set([]); // Clear existing data
            this.getListOfParties(); // Fetch new data based on the search query
          } else if (query.length === 0) {
            this.clearFilter(); // Clear filter if the input is empty
          }
        });
      }
      onSearchInputChange(event: Event): void {
        const inputElement = event.target as HTMLInputElement; // Type assertion
        const query = inputElement?.value ?? ''; // Safely access value
        this.searchSubject.next(query); // Push search input changes to the subject
      }

      onRowSelected(row: any): void {
        if (this.selectedRow === row) {
          // If already selected, unselect it
          this.selectedRow = null;
        } else {
          this.selectedRow = row;
        }
      }
      addPartyconfirmation() {
        if (this. data.multi && !this.data.route) {
          this.dialogRef.close(this.multiSelectedRows);
        } else if (!this. data.multi && !this.data.route)  {
          this.dialogRef.close(this.selectedRow);
        } else if(this.data.route){
          this.dialogRef.close();
          this.router.navigate(['/dashboard/parties'], {
        queryParams: this.selectedRow
      });
        }
      }
      
  public multiSelectedRows: Party[] = [];

onMultiSelectToggle(row: Party, isChecked: boolean): void {
  if (isChecked) {
    this.multiSelectedRows = [...this.multiSelectedRows, row];
  } else {
    this.multiSelectedRows = this.multiSelectedRows.filter(r => r.partiesId !== row.partiesId);
  }
}

  clearFilter() {
    this.isLoading=true;
    this.searchQuery = '';  // Clear the search query
    this.isLoading=false;
  }
  closeDialog() {
    this.dialogRef.close();
  }

  public getListOfParties(): void {
    if (this.loading || (this.partiesData().length >= this.totalItems && this.totalItems !== 0)) return;
    this.loading = true;

    const params: TAny = {
      caseId: this.data.caseId,
      name: this.searchQuery,
      page: this.currentpage,
      pageSize: 50
    };
    const body: TAny =  {
      partiesName: this.data.section==='party-name'?this.data.searchQuery:"",
      partiesId: this.data.section==='party-Id'?this.data.searchQuery:"",
      isCourtStaff: this.data.isCourtStaff?"Y":"N"
    };

    let httpreq;
    if (this.data.section === 'partiesRepresented') {
      httpreq = this.service.getcounsilPartyList(params);
    } else if (this.data.section === 'witness') {
      httpreq = this.service.getwitnessPartyList(params);
    } else if (this.data.section === 'parties') {
      httpreq = this.service.getaddedPartyList(params);
    } else if (this.data.section === 'party-name'||this.data.section === 'party-Id') {
      httpreq = this.service.searchPartiesIdName(body);
    } 
    else {
      console.error('Invalid section provided:', this.data.section);
      this.loading = false;
      return;
    }

    httpreq.subscribe({
      next: (res: IResponse<Party[]>) => {
  let receivedData: Party[] = [];

  if (res && Array.isArray(res.success) && res.success.length > 0) {
    receivedData = res.success;
    this.partiesData.update(prev => [...prev, ...res.success]);
  } else if (res && res.data && res.data.length > 0) {
    receivedData = res.data;
    this.partiesData.update(prev => [...prev, ...res.data]);
  }

  // ✅ 👉 HANDLE EMPTY DATA HERE
  if (receivedData.length === 0 && this.currentpage === 1) {
    this.loading = false;

    this.dialogRef.close(); // close current table dialog

    this.dialogService.openDialog1({
      data: {
        title: 'Information',
        message: 'No records found',
        icon: 'info'
      },
      disableClose: true,
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop',
    });

    return; // 🚨 IMPORTANT: stop further execution
  }

  this.totalItems = res.totalRecordsData || this.totalItems;
  this.loading = false;
  this.currentpage++;

  const combinedData = this.partiesData();
  if (combinedData.length === 1) {
    this.dialogRef.close(combinedData[0]);
  }
},
      error: (err) => {
        console.error('Error fetching parties:', err);  
        this.loading = false;
      },
    });
  }
  onScroll(event: any): void {
    if (this.data.section !== 'party-name'||this.data.section !== 'party-Id') {

    const target = event.target;
  
    // Use Math.round to ensure precise comparison
    const scrollHeight = Math.round(target.scrollHeight);
    const scrollTop = Math.round(target.scrollTop);
    const clientHeight = Math.round(target.clientHeight);
    console.log('ScrollHeight:', scrollHeight);
    console.log('ScrollTop:', scrollTop);
    console.log('ClientHeight:', clientHeight);
    console.log('Difference:', scrollHeight - scrollTop - clientHeight);
    if (scrollHeight - scrollTop === clientHeight) {
      console.log('Scrolled to bottom. Loading more data...');
      this.getListOfParties(); // Fetch the next batch of data
    }
  }}
}
